import React, { useState } from "react";
import axios from "axios";

export default function ImageEditor() {
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [editedImage, setEditedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImage(URL.createObjectURL(file));
      setEditedImage(null);
      setError(null);
    }
  };

  const handleEdit = async () => {
    if (!imageFile || !prompt.trim()) {
      setError("Please upload an image and enter an edit prompt.");
      return;
    }

    setLoading(true);
    setError(null);
    setEditedImage(null);

    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("prompt", prompt);

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const endpoint = apiUrl ? `${apiUrl}/chat/edit-image` : '/chat/edit-image';
      
      const response = await axios.post(
        endpoint,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.imageUrl) {
        setEditedImage(response.data.imageUrl);
      } else {
        setError("Failed to edit image. Please try again.");
      }
    } catch (err) {
      console.error("Image editing error:", err);
      setError(
        err.response?.data?.error || "Failed to edit image. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white/20 backdrop-blur-md rounded-xl shadow-md w-full max-w-md">
      <h2 className="text-lg font-semibold mb-4">DALL-E Image Editor</h2>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">
          Upload Image
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="mb-2 w-full text-sm"
        />
      </div>

      {image && (
        <div className="mb-4">
          <p className="text-sm font-medium mb-2">Original Image:</p>
          <img src={image} alt="Uploaded" className="w-full rounded-md border" />
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">
          Edit Prompt
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe how you want to edit the image (e.g., 'add a sunset in the background', 'remove the person', 'change the color to blue')"
          className="w-full p-2 border rounded-md text-sm"
          rows="3"
        />
      </div>

      <button
        onClick={handleEdit}
        disabled={loading || !imageFile || !prompt.trim()}
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full"
      >
        {loading ? "Editing..." : "Apply AI Edit"}
      </button>

      {error && (
        <div className="mt-4 p-2 bg-red-100 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {editedImage && (
        <div className="mt-4">
          <p className="text-sm font-medium mb-2">Edited Image:</p>
          <img
            src={editedImage}
            alt="Edited"
            className="w-full rounded-md border"
          />
          <a
            href={editedImage}
            download="edited-image.png"
            className="mt-2 inline-block px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm text-center w-full"
          >
            Download Edited Image
          </a>
        </div>
      )}

      <div className="mt-6 p-3 border border-dashed border-gray-300 rounded-md bg-white/40">
        <h3 className="text-sm font-semibold mb-2">Advanced image editing (Pro)</h3>
        <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
          <li>
            <strong>AI-powered tools</strong>: content-aware fill & object removal, AI upscaling & enhancement,
            style transfer, and generative edits driven by prompts.
          </li>
          <li>
            <strong>Layers & masking</strong>: non-destructive layers, clipping masks, layer masks, and smart
            objects for scalable assets.
          </li>
          <li>
            <strong>Color & tone</strong>: HDR toning, exposure blending, advanced curves, LUTs, and
            frequency-separation-style retouch workflows.
          </li>
          <li>
            <strong>Retouch & manipulation</strong>: precise liquify/warp, perspective & distortion correction,
            plus batch-style actions for repetitive edits.
          </li>
          <li>
            <strong>Vector & text</strong>: advanced typography (kerning, leading, baseline shift) and precise
            vector paths with the pen tool.
          </li>
        </ul>
      </div>
    </div>
  );
}
